import { useMemo } from "react";
import { Link } from "react-router-dom";
import { useSiteSettings } from "@/context/SiteSettingsContext";
import { useApiResource } from "@/hooks/useApiResource";
import { getMarqueeMessages } from "@/services/publicApi";
import { uploadUrl } from "@/services/config";
import { marqueeColor } from "@/config/marqueeColors";
import type { MarqueeMessage } from "@/types";

/** Fallback loop duration (seconds) when settings are unavailable. */
const DEFAULT_SPEED = 32;

/**
 * The site-wide announcement ticker. Shown at the top of every public page via
 * PublicLayout. It fetches the active admin messages itself and renders them as
 * one continuously scrolling line; hovering pauses it. Each message may link
 * somewhere (internal path or external URL) with an optional CTA label, carry
 * its own colour preset, and is separated from the next by the site favicon.
 *
 * The bar spans the full viewport width, and the scroll speed (seconds per
 * loop) is controlled live from Admin Console → System → Marquee messages.
 */
export default function MarqueeBar() {
  const { settings } = useSiteSettings();
  const { data } = useApiResource<MarqueeMessage[]>(() => getMarqueeMessages(), []);
  const messages = data ?? [];

  const active = useMemo(
    () =>
      messages
        .filter((m) => m.isActive && m.message.trim())
        .sort((a, b) => a.displayOrder - b.displayOrder),
    [messages],
  );

  if (!active.length) return null;

  const rawSpeed = Number(settings?.marqueeSpeed);
  const speed =
    Number.isFinite(rawSpeed) && rawSpeed > 0 ? Math.min(Math.max(rawSpeed, 5), 200) : DEFAULT_SPEED;

  // Repeat the list enough times that the moving block always overflows the
  // viewport, then mirror it for a seamless -50% loop.
  const repeat = Math.max(2, Math.ceil(8 / active.length));
  const block = Array.from({ length: repeat }, () => active).flat();
  const items = [...block, ...block];

  const faviconSrc = settings?.faviconUrl ? uploadUrl(settings.faviconUrl) : "";

  const separator = (
    <span className="mx-4 flex h-6 w-6 shrink-0 items-center justify-center" aria-hidden="true">
      {faviconSrc ? (
        <img src={faviconSrc} alt="" className="h-6 w-6 rounded-sm object-contain" />
      ) : (
        <i className="ri-megaphone-fill text-base text-primary-600"></i>
      )}
    </span>
  );

  return (
    <div className="w-full border-b border-primary-200/70 bg-primary-50">
      <div className="flex w-full items-stretch">
        <div className="flex shrink-0 items-center gap-2 bg-primary-500 px-3 py-2 text-foreground-950 md:px-4">
          <span className="flex h-4 w-4 items-center justify-center">
            <i className="ri-megaphone-fill text-sm"></i>
          </span>
          <span className="hidden text-xs font-bold uppercase tracking-wide sm:inline">Announcements</span>
          <span className="text-xs font-bold uppercase tracking-wide sm:hidden">News</span>
        </div>

        <div className="marquee-track relative flex-1 overflow-hidden">
          <div
            className="animate-marquee flex w-max items-center py-2"
            style={{ animationDuration: `${speed}s` }}
          >
            {items.map((m, index) => {
              const tone = marqueeColor(m.color);
              const body = (
                <>
                  <span className={`text-sm font-medium transition-colors group-hover:underline ${tone.text}`}>
                    {m.message}
                  </span>
                  {m.linkLabel && (
                    <span className={`rounded-full px-2 py-0.5 text-[11px] font-semibold ${tone.chip}`}>
                      {m.linkLabel}
                    </span>
                  )}
                </>
              );
              return (
                <span key={`${m.id}-${index}`} className="inline-flex shrink-0 items-center">
                  {separator}
                  {m.linkUrl ? (
                    m.linkUrl.startsWith("http") ? (
                      <a
                        href={m.linkUrl}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="group inline-flex items-center gap-2 whitespace-nowrap cursor-pointer"
                      >
                        {body}
                      </a>
                    ) : (
                      <Link
                        to={m.linkUrl}
                        className="group inline-flex items-center gap-2 whitespace-nowrap cursor-pointer"
                      >
                        {body}
                      </Link>
                    )
                  ) : (
                    <span className="inline-flex items-center gap-2 whitespace-nowrap">{body}</span>
                  )}
                </span>
              );
            })}
          </div>
        </div>
      </div>
    </div>
  );
}